/**
 * Shared helpers for visual config transforms.
 *
 * Small utilities used across multiple transform modules:
 * - Field-mapping label resolution
 * - Safe single-object axis/legend extraction
 * - OLS regression computation
 */
import type { FieldMappingMap } from '../../../../../api/types';

// ── Type guards ────────────────────────────────────────────────────────────

/** True if `v` is a non-array plain object (used for ECharts axis/legend/tooltip). */
export function isSingleObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

// ── Label resolution ───────────────────────────────────────────────────────

/**
 * Resolve a human-readable label from fieldMappings.
 * Falls back to the raw column name if no label is defined.
 */
export function resolveLabel(
  fieldMappings: FieldMappingMap | null | undefined,
  axis: 'x' | 'y' | 'series',
  fallback: string,
): string {
  if (!fieldMappings) return fallback;
  const fm = fieldMappings as Record<string, any>;
  if (axis === 'x') return fm.x?.label ?? fallback;
  if (axis === 'y') {
    const yArr = fm.y;
    if (Array.isArray(yArr) && yArr.length > 0) return yArr[0].label ?? fallback;
    return fallback;
  }
  if (axis === 'series') return fm.series?.label ?? fallback;
  return fallback;
}

// ── OLS regression ─────────────────────────────────────────────────────────

export interface RegressionResult {
  slope: number;
  intercept: number;
  minX: number;
  maxX: number;
}

/**
 * Compute OLS (ordinary least squares) linear regression from scatter data.
 *
 * Each point is expected as `[x, y, ...]`.
 * Returns null if fewer than 2 valid numeric points exist.
 */
export function computeOLSRegression(data: unknown[]): RegressionResult | null {
  let validN = 0;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  let minX = Infinity, maxX = -Infinity;

  for (const pt of data) {
    const px = Number(Array.isArray(pt) ? pt[0] : 0);
    const py = Number(Array.isArray(pt) ? pt[1] : 0);
    if (isNaN(px) || isNaN(py)) continue;
    sumX += px; sumY += py; sumXY += px * py; sumX2 += px * px;
    if (px < minX) minX = px;
    if (px > maxX) maxX = px;
    validN++;
  }

  const denom = validN * sumX2 - sumX * sumX;
  if (denom === 0 || validN < 2 || !isFinite(minX) || !isFinite(maxX)) return null;

  return {
    slope: (validN * sumXY - sumX * sumY) / denom,
    intercept: (sumY - ((validN * sumXY - sumX * sumY) / denom) * sumX) / validN,
    minX,
    maxX,
  };
}